import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Star, MapPin, Phone, Mail, Globe, CheckCircle, Shield,
  Award, Users, Briefcase, Building2, Calendar, DollarSign,
  ChevronRight, ChevronLeft, Send, ArrowLeft, Instagram, Linkedin, Twitter,
  Play, ExternalLink, Clock, TrendingUp, Flag, Package, Wrench,
  Loader2, X, ZoomIn, Images, ImageIcon, LayoutGrid, List, Facebook, Tag
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function slugifyName(str = '') {
  return str.toLowerCase().trim()
    .replace(/ğ/g,'g').replace(/ü/g,'u').replace(/ş/g,'s')
    .replace(/ı/g,'i').replace(/ö/g,'o').replace(/ç/g,'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

// Resolve S3 asset paths returned by the upload API
const resolveAssetUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${process.env.REACT_APP_BACKEND_URL}/api/contractor/profile/assets/${path}`;
};

// Default fallback data for empty profiles
const DEFAULT_DATA = {
  tagline: 'Professional Exhibition Stand Builder',
  logo: 'https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg',
  coverImage: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2200&auto=format&fit=crop',
  about: 'No company description yet. Update your profile in the Contractor Admin Panel.',
  stats: [
    { label: 'Projects Completed', value: '0', icon: Briefcase },
    { label: 'Countries Served', value: '0', icon: Globe },
    { label: 'Happy Clients', value: '0', icon: Users },
    { label: 'Years in Business', value: '0', icon: Clock },
  ],
  portfolio: [], services: [], team: [],
  coverage: { countries: [], activeShows: [] },
  memberSince: '—', responseTime: '< 24 hours', rating: 0, reviewCount: 0,
  reviews: [], ratingBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  social: {}, location: '—', email: '—', phone: '—', website: '—',
  verified: false, premium: false,
};

const mapApiToData = (apiData) => {
  const org = apiData.org || {};
  const p   = apiData.profile || {};
  const stats = p.stats || {};
  const social = p.social || {};

  return {
    name: org.display_name || '—',
    orgId: org.id,
    slug: org.slug || '',
    tagline: p.tagline || DEFAULT_DATA.tagline,
    logo: resolveAssetUrl(p.logo_url) || DEFAULT_DATA.logo,
    coverColor: p.cover_color || '#0B1E4D',
    about: p.about || DEFAULT_DATA.about,
    memberSince: p.member_since || DEFAULT_DATA.memberSince,
    responseTime: p.response_time || DEFAULT_DATA.responseTime,
    location: [org.city, org.country].filter(Boolean).join(', ') || '—',
    website: social.website || org.website || '—',
    email: '—', phone: '—',
    verified: org.status === 'ACTIVE' || (org.is_verified === true && org.verified_until && org.verified_until > new Date().toISOString()),
    premium: false,
    rating: 0, reviewCount: 0,
    stats: [
      { label: 'Projects Completed', value: String(stats.projects_completed || 0), icon: Briefcase },
      { label: 'Countries Served',   value: String(stats.countries_served   || 0), icon: Globe },
      { label: 'Happy Clients',      value: String(stats.happy_clients || stats.team_size || 0), icon: Users },
      { label: 'Years in Business',  value: String(stats.years_in_business  || stats.years_experience || 0), icon: Clock },
    ],
    portfolio: (p.portfolio || []).map(item => ({
      id: item.id || item.title,
      title: item.title,
      client: item.client || '',
      sqm: item.sqm || 0,
      year: item.year || '',
      country: item.country || '',
      city: item.city || '',
      description: item.description || '',
      image: resolveAssetUrl(item.image_url) || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=900&auto=format&fit=crop',
      images: (item.images || []).length > 0
        ? item.images
        : item.image_url
          ? [{ id: `img_${item.id}`, url: item.image_url, caption: '' }]
          : [],
      tags: item.tags || [],
    })),
    services: (p.services_detail || []).map(svc => ({
      icon: Wrench,
      title: svc.title,
      desc: svc.desc || '',
      price: svc.price || '',
    })),
    team: (p.team || []).map(m => ({
      name: m.name, role: m.role, bio: m.bio || '',
      avatar: resolveAssetUrl(m.avatar_url) || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=random`,
      linkedin: m.linkedin || '#',
    })),
    coverage: {
      countries: (p.coverage?.countries || []),
      activeShows: p.coverage?.active_shows || [],
      volumePricingShows: p.coverage?.volume_pricing_shows || [],
    },
    reviews: DEFAULT_DATA.reviews,
    ratingBreakdown: DEFAULT_DATA.ratingBreakdown,
    social: {
      linkedin: social.linkedin,
      instagram: social.instagram,
      twitter: social.twitter,
    },
  };
};

const TABS = [
  { id: 'overview',  label: 'Overview' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'services',  label: 'Services' },
  { id: 'team',      label: 'Team' },
  { id: 'coverage',  label: 'Coverage' },
  { id: 'reviews',   label: 'Reviews' },
  { id: 'contact',   label: 'Get in Touch' },
];

const Stars = ({ rating, size = 'md' }) => {
  const sz = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(s => (
        <Star key={s} className={`${sz} ${s <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
      ))}
    </div>
  );
};

/* ─── OVERVIEW TAB ─────────────────────────────────────── */
const OverviewTab = ({ d, onProjectClick, onViewAll }) => (
  <div className="space-y-10">
    {/* Stats Row */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {d.stats.map((s, i) => {
        const Icon = s.icon;
        return (
          <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm text-center hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mx-auto mb-3">
              <Icon className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-2xl font-black text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        );
      })}
    </div>

    {/* About */}
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
      <h3 className="text-xl font-bold text-gray-900 mb-4">About Us</h3>
      <div className="space-y-4">
        {d.about.split('\n\n').map((para, i) => (
          <p key={i} className="text-gray-600 leading-relaxed text-[15px]">{para}</p>
        ))}
      </div>
    </div>

    {/* Quick Portfolio Preview */}
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900">Recent Projects</h3>
        <button onClick={onViewAll} className="text-orange-600 text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all">View all <ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {d.portfolio.slice(0, 3).map(p => (
          <div key={p.id} onClick={() => onProjectClick && onProjectClick(p)}
            className="group relative rounded-xl overflow-hidden aspect-[4/3] cursor-pointer">
            <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="absolute bottom-3 left-3 right-3">
              <p className="text-white text-sm font-semibold leading-tight">{p.title}</p>
              <p className="text-white/70 text-xs mt-0.5">{p.sqm} sqm · {p.year}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Active Shows */}
    <div className="bg-gradient-to-r from-[#0B1E4D] to-[#163A7D] rounded-2xl p-6 text-white">
      <h3 className="font-bold text-lg mb-4">Active at These Trade Shows</h3>
      <div className="flex flex-wrap gap-2">
        {d.coverage.activeShows.map(show => (
          <span key={show} className="bg-white/15 border border-white/20 px-3 py-1.5 rounded-full text-sm font-medium">{show}</span>
        ))}
      </div>
    </div>
  </div>
);

/* ─── PORTFOLIO LIGHTBOX ────────────────────────────────── */
const PortfolioLightbox = ({ project, startIdx = 0, onClose }) => {
  const [idx, setIdx] = React.useState(startIdx);
  const images = project.images?.length ? project.images : [{ url: project.image, caption: '' }];
  const total = images.length;
  const src = images[idx]?.url ? resolveAssetUrl(images[idx].url) : null;
  const caption = images[idx]?.caption || '';

  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') setIdx(i => (i + 1) % total);
      if (e.key === 'ArrowLeft')  setIdx(i => (i - 1 + total) % total);
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [total, onClose]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col" onClick={onClose}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 text-white" onClick={e => e.stopPropagation()}>
        <div>
          <h3 className="font-bold text-lg leading-tight">{project.title}</h3>
          <p className="text-sm text-white/60">{project.client} · {project.year} {project.sqm ? `· ${project.sqm} m²` : ''}</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-white/50 font-mono">{idx + 1} / {total}</span>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Main image */}
      <div className="flex-1 flex items-center justify-center relative px-16 min-h-0" onClick={e => e.stopPropagation()}>
        {total > 1 && (
          <button onClick={() => setIdx(i => (i - 1 + total) % total)}
            className="absolute left-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors z-10">
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
        )}
        <div className="relative max-h-full max-w-full flex items-center justify-center">
          {src ? (
            <img src={src} alt={project.title} className="max-h-[65vh] max-w-full object-contain rounded-xl shadow-2xl" />
          ) : (
            <div className="w-80 h-60 bg-gray-800 rounded-xl flex items-center justify-center">
              <ImageIcon className="w-12 h-12 text-gray-600" />
            </div>
          )}
          {caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3 rounded-b-xl">
              <p className="text-white text-sm">{caption}</p>
            </div>
          )}
        </div>
        {total > 1 && (
          <button onClick={() => setIdx(i => (i + 1) % total)}
            className="absolute right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors z-10">
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {total > 1 && (
        <div className="flex gap-2 justify-center px-6 py-4 overflow-x-auto" onClick={e => e.stopPropagation()}>
          {images.map((img, ti) => {
            const tSrc = resolveAssetUrl(img.url);
            return (
              <button key={ti} onClick={() => setIdx(ti)}
                className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${ti === idx ? 'border-orange-400 scale-110' : 'border-transparent opacity-60 hover:opacity-90'}`}>
                {tSrc
                  ? <img src={tSrc} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gray-700 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-gray-500" /></div>
                }
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── PORTFOLIO TAB ─────────────────────────────────────── */
const PortfolioTab = ({ d }) => {
  const [tagFilter, setTagFilter] = React.useState('All');
  const [yearFilter, setYearFilter] = React.useState('All');
  const [countryFilter, setCountryFilter] = React.useState('All');
  const [lightbox, setLightbox] = React.useState(null); // { project, startIdx }
  const [viewMode, setViewMode] = React.useState('grid'); // 'grid' | 'list'

  const allTags    = ['All', ...new Set(d.portfolio.flatMap(p => p.tags || []))].filter(Boolean);
  const allYears   = ['All', ...new Set(d.portfolio.map(p => p.year).filter(Boolean)).values()].sort((a,b) => b-a);
  const allCountries = ['All', ...new Set(d.portfolio.map(p => p.country).filter(Boolean))];

  const filtered = d.portfolio.filter(p => {
    const tagOk     = tagFilter     === 'All' || (p.tags || []).includes(tagFilter);
    const yearOk    = yearFilter    === 'All' || String(p.year) === String(yearFilter);
    const countryOk = countryFilter === 'All' || p.country === countryFilter;
    return tagOk && yearOk && countryOk;
  });

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Tag filter */}
        {allTags.length > 1 && (
          <div className="flex gap-1.5 flex-wrap">
            {allTags.map(t => (
              <button key={t} onClick={() => setTagFilter(t)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${tagFilter === t ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-300'}`}>
                {t}
              </button>
            ))}
          </div>
        )}
        {/* Year filter */}
        {allYears.length > 2 && (
          <div className="flex gap-1.5">
            {allYears.map(y => (
              <button key={y} onClick={() => setYearFilter(y)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${yearFilter === String(y) ? 'bg-[#0B2A5C] text-white border-[#0B2A5C]' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                {y}
              </button>
            ))}
          </div>
        )}
        {/* Country filter */}
        {allCountries.length > 2 && (
          <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
            className="px-3 py-1.5 rounded-full text-xs font-medium border border-gray-200 text-gray-600 bg-white focus:outline-none">
            {allCountries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {/* View mode */}
        <div className="ml-auto flex gap-1 bg-gray-100 p-0.5 rounded-lg">
          <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow' : 'text-gray-500'}`}>
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow' : 'text-gray-500'}`}>
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Result count */}
      <p className="text-xs text-gray-400">{filtered.length} proje gösteriliyor</p>

      {/* Grid view */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(p => {
            const images = p.images?.length ? p.images : (p.image ? [{ url: p.image }] : []);
            const coverSrc = images[0]?.url ? resolveAssetUrl(images[0].url) : null;
            const imgCount = images.length;
            return (
              <div key={p.id} onClick={() => setLightbox({ project: p, startIdx: 0 })}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                  {coverSrc
                    ? <img src={coverSrc} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-8 h-8 text-gray-300" /></div>
                  }
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                      <ZoomIn className="w-5 h-5 text-gray-800" />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full font-medium">{p.sqm} m²</span>
                    {imgCount > 1 && (
                      <span className="bg-orange-500/90 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                        <Images className="w-3 h-3" />{imgCount}
                      </span>
                    )}
                  </div>
                  {/* Thumbnail strip preview */}
                  {imgCount > 1 && (
                    <div className="absolute bottom-0 left-0 right-0 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {images.slice(0, 5).map((img, ti) => {
                        const ts = resolveAssetUrl(img.url);
                        return ts ? (
                          <div key={ti} className="flex-1 h-8 overflow-hidden">
                            <img src={ts} alt="" className="w-full h-full object-cover" />
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h4 className="font-bold text-gray-900 text-sm leading-snug mb-1">{p.title}</h4>
                  <p className="text-xs text-gray-400 mb-2">{[p.client, p.year, p.city].filter(Boolean).join(' · ')}</p>
                  {(p.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {p.tags.slice(0, 3).map(t => (
                        <span key={t} className="bg-orange-50 text-orange-700 text-xs px-2 py-0.5 rounded-full border border-orange-100">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List view */}
      {viewMode === 'list' && (
        <div className="space-y-3">
          {filtered.map(p => {
            const images = p.images?.length ? p.images : (p.image ? [{ url: p.image }] : []);
            const coverSrc = images[0]?.url ? resolveAssetUrl(images[0].url) : null;
            return (
              <div key={p.id} onClick={() => setLightbox({ project: p, startIdx: 0 })}
                className="flex gap-4 bg-white border border-gray-100 rounded-xl p-3 hover:shadow-md transition-all cursor-pointer group">
                <div className="w-24 h-18 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 aspect-[4/3]">
                  {coverSrc
                    ? <img src={coverSrc} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-6 h-6 text-gray-300" /></div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-900 text-sm mb-0.5">{p.title}</h4>
                  <p className="text-xs text-gray-500">{[p.client, p.year, p.city].filter(Boolean).join(' · ')}</p>
                  {p.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{p.description}</p>}
                </div>
                <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                  {p.sqm && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{p.sqm} m²</span>}
                  {images.length > 1 && <span className="text-xs text-orange-500 flex items-center gap-0.5"><Images className="w-3 h-3" />{images.length}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>Bu filtreyle proje bulunamadı</p>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <PortfolioLightbox
          project={lightbox.project}
          startIdx={lightbox.startIdx}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

/* ─── SERVICES TAB ─────────────────────────────────────── */
const ServicesTab = ({ d }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
    {d.services.map((s, i) => {
      const Icon = s.icon;
      return (
        <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center flex-shrink-0 group-hover:from-orange-500 group-hover:to-orange-600 transition-all duration-300">
              <Icon className="w-6 h-6 text-orange-500 group-hover:text-white transition-colors duration-300" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-900 mb-1">{s.title}</h4>
              <p className="text-sm text-gray-500 leading-relaxed mb-3">{s.desc}</p>
              <span className="inline-block bg-gray-50 border border-gray-200 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full">{s.price}</span>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

/* ─── TEAM TAB ─────────────────────────────────────── */
const TeamTab = ({ d, slug }) => {
  const navigate = useNavigate();

  return (
  <div className="space-y-6">
    <p className="text-gray-500 text-sm max-w-xl">Meet the passionate professionals behind {d.name}. A multidisciplinary team united by a love for exceptional exhibition experiences.</p>

    {/* Team Grid — click opens separate member page */}
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {d.team.map((m, i) => (
        <div key={i}
          onClick={() => navigate(`/contractor/${slug}/team/${slugifyName(m.name)}`)}
          className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg hover:border-orange-200 transition-all duration-300 hover:-translate-y-1 text-center group cursor-pointer">
          <div className="relative aspect-square overflow-hidden bg-gray-100">
            {m.avatar
              ? <img src={m.avatar} alt={m.name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
              : <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-gray-400"
                  style={{ background: `hsl(${(i * 60 + 200) % 360},40%,85%)` }}>
                  {m.name.split(' ').map(n => n[0]).join('').slice(0,2)}
                </div>
            }
            <div className="absolute inset-0 bg-black/0 group-hover:bg-orange-500/10 transition-colors flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100">
              <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full font-semibold">View Profile →</span>
            </div>
          </div>
          <div className="p-4">
            <h4 className="font-bold text-gray-900 text-sm">{m.name}</h4>
            <p className="text-xs text-orange-600 font-semibold mb-1">{m.role}</p>
            {(m.linkedin || m.instagram || m.facebook) && (
              <div className="flex justify-center gap-1.5 mt-2">
                {m.linkedin  && <span className="w-5 h-5 bg-[#0A66C2] rounded-full flex items-center justify-center"><Linkedin className="w-2.5 h-2.5 text-white" /></span>}
                {m.instagram && <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{background:'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)'}}><Instagram className="w-2.5 h-2.5 text-white" /></span>}
                {m.facebook  && <span className="w-5 h-5 bg-[#1877F2] rounded-full flex items-center justify-center"><Facebook className="w-2.5 h-2.5 text-white" /></span>}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};

/* ─── COVERAGE TAB ─────────────────────────────────────── */
const CoverageTab = ({ d, onContactRequest }) => {
  const handleVolumeQuote = () => {
    const shows = d.coverage.volumePricingShows || [];
    const msg = `Hello,\n\nI'm interested in your volume pricing for the following trade shows:\n${shows.map(s => `• ${s}`).join('\n')}\n\nI understand you'll already have a team on-site for these events and can offer significantly better rates. Could you please send me a quote?\n\nThank you.`;
    if (onContactRequest) onContactRequest(msg);
  };

  return (
  <div className="space-y-8">
    {/* Country + Cities Grid */}
    <div>
      <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
        <Globe className="w-5 h-5 text-[#0B1E4D]" /> Countries & Cities
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {d.coverage.countries.map((c, i) => (
          <div key={i} className={`rounded-2xl p-4 border ${c.primary ? 'bg-[#0B1E4D] border-[#0B1E4D] text-white' : 'bg-white border-gray-100 shadow-sm hover:shadow-md transition-shadow'}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{c.flag}</span>
              <div>
                <p className={`font-bold text-sm ${c.primary ? 'text-white' : 'text-gray-900'}`}>{c.name}</p>
                {c.primary && <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">Home Base</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {c.cities && c.cities.map(city => (
                <span key={city} className={`text-xs px-2 py-0.5 rounded-full border ${c.primary ? 'border-white/30 bg-white/15 text-white/90' : 'border-gray-200 text-gray-600 bg-gray-50'}`}>{city}</span>
              ))}
            </div>
          </div>
        ))}
        {d.coverage.countries.length === 0 && (
          <div className="col-span-4 text-center py-8 text-gray-400 text-sm">No coverage data yet</div>
        )}
      </div>
    </div>

    {/* Active Trade Shows */}
    {d.coverage.activeShows && d.coverage.activeShows.length > 0 && (
      <div>
        <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-orange-500" /> Active at These Trade Shows
        </h3>
        <div className="bg-gradient-to-r from-[#0B1E4D] to-[#163A7D] rounded-2xl p-6 text-white">
          <div className="flex flex-wrap gap-2 mb-4">
            {d.coverage.activeShows.map(show => (
              <Link key={show}
                to={`/find-contractors/fair/${encodeURIComponent(show.toLowerCase().replace(/\s+/g, '-'))}`}
                className="bg-white/15 hover:bg-white/25 border border-white/20 px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5"
              >
                ⭐ {show}
              </Link>
            ))}
          </div>
          <p className="text-blue-200 text-xs">Click a trade show to find other contractors active at that event</p>
        </div>
      </div>
    )}

    {/* Volume Pricing Shows */}
    {d.coverage.volumePricingShows && d.coverage.volumePricingShows.length > 0 && (
      <div>
        <h3 className="font-bold text-gray-900 text-lg mb-1 flex items-center gap-2">
          <Tag className="w-5 h-5 text-emerald-600" /> Volume Pricing Shows
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          We're already sending our team to these fairs every year — meaning we can offer significantly lower rates compared to one-off projects. No extra mobilization costs, no logistics surprises.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          {d.coverage.volumePricingShows.map((show, i) => (
            <div key={i}
              className="bg-white border border-emerald-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 group">
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="font-semibold text-gray-900 text-sm leading-tight group-hover:text-emerald-700 transition-colors">{show}</p>
                <span className="flex-shrink-0 text-xs bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Better Rate</span>
              </div>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-emerald-500" />
                Already attending — reduced mobilization cost
              </p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-emerald-50 to-orange-50 border border-emerald-100 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">Save up to 30% on your stand</p>
              <p className="text-xs text-gray-500 mt-0.5">Send a brief for any of these shows and mention volume pricing — we'll prioritize your quote.</p>
            </div>
          </div>
          <button
            onClick={handleVolumeQuote}
            className="flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all whitespace-nowrap shadow-sm"
          >
            Request Volume Quote
          </button>
        </div>
      </div>
    )}

    <div className="bg-orange-50 border border-orange-100 rounded-2xl p-6">
      <div className="flex items-start gap-3">
        <Flag className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-gray-900 mb-1">International Projects</p>
          <p className="text-sm text-gray-600">We manage all international projects in-house including logistics, local permits, customs clearance, and on-site supervision. No third-party middlemen — just our dedicated team.</p>
        </div>
      </div>
    </div>
  </div>
  );
};

/* ─── REVIEWS TAB ─────────────────────────────────────── */
const ReviewsTab = ({ d }) => {
  const total = Object.values(d.ratingBreakdown).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-8">
      {/* Rating Summary */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 flex flex-col sm:flex-row gap-8">
        <div className="text-center sm:border-r sm:border-gray-100 sm:pr-8 flex-shrink-0">
          <p className="text-6xl font-black text-gray-900">{d.rating}</p>
          <Stars rating={d.rating} size="lg" />
          <p className="text-sm text-gray-400 mt-2">{d.reviewCount} reviews</p>
        </div>
        <div className="flex-1 space-y-2">
          {[5, 4, 3, 2, 1].map(star => {
            const count = d.ratingBreakdown[star] || 0;
            const pct = total ? Math.round((count / total) * 100) : 0;
            return (
              <div key={star} className="flex items-center gap-3">
                <div className="flex items-center gap-1 w-8">
                  <span className="text-sm text-gray-600">{star}</span>
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                </div>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-gray-400 w-8 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {d.reviews.map(r => (
          <div key={r.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <img src={r.avatar} alt={r.author} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="font-bold text-gray-900 text-sm">{r.author}</p>
                  <p className="text-xs text-gray-500">{r.company}</p>
                </div>
              </div>
              <div className="text-right">
                <Stars rating={r.rating} />
                <p className="text-xs text-gray-400 mt-1">{r.date}</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed italic">"{r.text}"</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── CONTACT TAB ─────────────────────────────────────── */
const ContactTab = ({ d }) => {
  const { slug } = useParams();
  const [tab, setTab] = useState('brief');
  const [briefForm, setBriefForm] = useState({ name: '', company: '', email: '', phone: '', city: '', show: '', dates: '', budget: '', sqm: '', notes: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Listen for prefill from Volume Pricing CTA
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail?.message || '';
      setTab('message');
      setContactForm(p => ({ ...p, subject: 'Volume Pricing Inquiry', message: msg }));
    };
    window.addEventListener('prefillContact', handler);
    return () => window.removeEventListener('prefillContact', handler);
  }, []);

  const submitBrief = async (e) => {
    e.preventDefault();
    setLoading(true); setApiError(null);
    try {
      await axios.post(`${API}/public/inquiry`, {
        target_slug: slug || d.slug,
        target_type: 'contractor',
        inquiry_type: 'brief',
        name: briefForm.name,
        company: briefForm.company,
        email: briefForm.email,
        phone: briefForm.phone,
        show_name: briefForm.show,
        city: briefForm.city,
        event_dates: briefForm.dates,
        budget: briefForm.budget,
        stand_sqm: briefForm.sqm,
        notes: briefForm.notes,
      });
      setSubmitted('brief');
    } catch (err) {
      setApiError('Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally { setLoading(false); }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setLoading(true); setApiError(null);
    try {
      await axios.post(`${API}/public/inquiry`, {
        target_slug: slug || d.slug,
        target_type: 'contractor',
        inquiry_type: 'contact',
        name: contactForm.name,
        email: contactForm.email,
        subject: contactForm.subject,
        message: contactForm.message,
      });
      setSubmitted('contact');
    } catch (err) {
      setApiError('Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally { setLoading(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Left: Quick Contact Info */}
      <div className="space-y-4">
        <div className="bg-[#0B1E4D] rounded-2xl p-6 text-white">
          <h3 className="font-bold text-lg mb-4">Contact Details</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center"><Mail className="w-4 h-4 text-white" /></div>
              <div><p className="text-xs text-white/60">Email</p><p className="text-sm font-medium">{d.email}</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center"><Phone className="w-4 h-4 text-white" /></div>
              <div><p className="text-xs text-white/60">Phone</p><p className="text-sm font-medium">{d.phone}</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center"><Globe className="w-4 h-4 text-white" /></div>
              <div><p className="text-xs text-white/60">Website</p><p className="text-sm font-medium">{d.website}</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center"><MapPin className="w-4 h-4 text-white" /></div>
              <div><p className="text-xs text-white/60">Location</p><p className="text-sm font-medium">{d.location}</p></div>
            </div>
          </div>
        </div>
        <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-orange-500" />
            <p className="font-semibold text-gray-900 text-sm">Fast Response</p>
          </div>
          <p className="text-xs text-gray-500">Average response time: <strong className="text-orange-600">{d.responseTime}</strong>. We're ready to discuss your next show.</p>
        </div>
      </div>

      {/* Right: Forms */}
      <div className="lg:col-span-2">
        {submitted ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {submitted === 'brief' ? 'Brief Submitted!' : 'Message Sent!'}
            </h3>
            <p className="text-gray-500 text-sm mb-6">
              {submitted === 'brief'
                ? `${d.name} will review your brief and respond within 2 hours.`
                : 'Your message has been received. They will get back to you shortly.'}
            </p>
            <button onClick={() => setSubmitted(null)} className="text-orange-600 text-sm font-semibold hover:underline">Send another message</button>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            {/* Tab toggle */}
            <div className="flex border-b border-gray-100">
              <button onClick={() => setTab('brief')} className={`flex-1 py-4 text-sm font-semibold transition-colors ${tab === 'brief' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`}>
                Submit a Brief
              </button>
              <button onClick={() => setTab('contact')} className={`flex-1 py-4 text-sm font-semibold transition-colors ${tab === 'contact' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`}>
                Quick Contact
              </button>
            </div>

            {tab === 'brief' ? (
              <form onSubmit={submitBrief} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Full Name *</label><Input value={briefForm.name} onChange={e => setBriefForm(p => ({...p, name: e.target.value}))} placeholder="John Smith" required className="h-10" /></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Company *</label><Input value={briefForm.company} onChange={e => setBriefForm(p => ({...p, company: e.target.value}))} placeholder="Your Company" required className="h-10" /></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Email *</label><Input type="email" value={briefForm.email} onChange={e => setBriefForm(p => ({...p, email: e.target.value}))} placeholder="john@company.com" required className="h-10" /></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Phone</label><Input value={briefForm.phone} onChange={e => setBriefForm(p => ({...p, phone: e.target.value}))} placeholder="+1 234 567 0000" className="h-10" /></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Show / Fair Name *</label><Input value={briefForm.show} onChange={e => setBriefForm(p => ({...p, show: e.target.value}))} placeholder="e.g. Hannover Messe 2025" required className="h-10" /></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">City</label><Input value={briefForm.city} onChange={e => setBriefForm(p => ({...p, city: e.target.value}))} placeholder="Frankfurt, Germany" className="h-10" /></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Event Dates</label><Input value={briefForm.dates} onChange={e => setBriefForm(p => ({...p, dates: e.target.value}))} placeholder="Apr 1-5, 2025" className="h-10" /></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Budget (USD/EUR)</label><Input value={briefForm.budget} onChange={e => setBriefForm(p => ({...p, budget: e.target.value}))} placeholder="e.g. $15,000 – $25,000" className="h-10" /></div>
                  <div className="sm:col-span-2"><label className="text-xs font-semibold text-gray-500 mb-1 block">Stand Size (sqm)</label><Input value={briefForm.sqm} onChange={e => setBriefForm(p => ({...p, sqm: e.target.value}))} placeholder="e.g. 48 sqm (6m x 8m)" className="h-10" /></div>
                </div>
                <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Project Notes / Requirements</label><Textarea value={briefForm.notes} onChange={e => setBriefForm(p => ({...p, notes: e.target.value}))} placeholder="Describe your stand requirements, design preferences, key objectives..." rows={4} /></div>
                <Button type="submit" data-testid="submit-brief-btn" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 h-11 font-semibold">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit Brief to {d.name}
                </Button>
                {apiError && <p className="text-red-500 text-sm text-center">{apiError}</p>}
              </form>
            ) : (
              <form onSubmit={submitContact} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Name *</label><Input value={contactForm.name} onChange={e => setContactForm(p => ({...p, name: e.target.value}))} placeholder="Your Name" required className="h-10" /></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Email *</label><Input type="email" value={contactForm.email} onChange={e => setContactForm(p => ({...p, email: e.target.value}))} placeholder="your@email.com" required className="h-10" /></div>
                  <div className="sm:col-span-2"><label className="text-xs font-semibold text-gray-500 mb-1 block">Subject</label><Input value={contactForm.subject} onChange={e => setContactForm(p => ({...p, subject: e.target.value}))} placeholder="e.g. Quote request for Automechanika 2025" className="h-10" /></div>
                </div>
                <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Message *</label><Textarea value={contactForm.message} onChange={e => setContactForm(p => ({...p, message: e.target.value}))} placeholder="Write your message..." rows={5} required /></div>
                <Button type="submit" data-testid="submit-contact-btn" disabled={loading} className="w-full bg-[#0B1E4D] hover:bg-[#163A7D] h-11 font-semibold">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Message
                </Button>
                {apiError && <p className="text-red-500 text-sm text-center">{apiError}</p>}
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── REQUEST QUOTE MODAL (for authenticated Exhibitors) ─── */
const CURRENCIES = ['EUR', 'USD', 'GBP', 'AED', 'TRY'];

const RequestQuoteModal = ({ contractorOrgId, contractorName, onClose, token }) => {
  const [form, setForm] = useState({
    show: '', country: '', city: '', month: '',
    w_m: '6', d_m: '6',
    budget_min: '0', budget_max: '', currency: 'EUR',
    deadline_at: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API}/exhibitor/briefs`, {
        event: {
          show: form.show,
          country: form.country.toUpperCase().slice(0, 2),
          city: form.city,
          month: form.month,
        },
        stand: {
          w_m: parseFloat(form.w_m) || 1,
          d_m: parseFloat(form.d_m) || 1,
          area_sqm: (parseFloat(form.w_m) || 1) * (parseFloat(form.d_m) || 1),
          indoor: true,
        },
        budget: {
          min: parseFloat(form.budget_min) || 0,
          max: parseFloat(form.budget_max) || 1,
          currency: form.currency,
        },
        deadline_at: form.deadline_at,
        notes: form.notes || null,
        targeted_contractor_id: contractorOrgId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Request a Quote</h2>
            <p className="text-xs text-gray-500 mt-0.5">Sending directly to <span className="font-semibold text-orange-600">{contractorName}</span></p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors text-lg leading-none">×</button>
        </div>

        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Brief Sent!</h3>
            <p className="text-sm text-gray-500 mb-6">Your brief has been sent to {contractorName}. They will respond shortly.</p>
            <button onClick={onClose} className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-xl font-semibold text-sm transition-colors">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Event Info */}
            <div>
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">Event Details</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Show / Fair Name *</label>
                  <input value={form.show} onChange={e => upd('show', e.target.value)} required placeholder="e.g. Hannover Messe 2025"
                    className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">Country Code * <span className="text-gray-400 font-normal">(2 letters, e.g. DE)</span></label>
                    <input value={form.country} onChange={e => upd('country', e.target.value)} required maxLength={2} placeholder="DE"
                      className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 uppercase" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">City *</label>
                    <input value={form.city} onChange={e => upd('city', e.target.value)} required placeholder="Frankfurt"
                      className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">Event Month * <span className="text-gray-400 font-normal">(YYYY-MM)</span></label>
                    <input value={form.month} onChange={e => upd('month', e.target.value)} required placeholder="2025-06" pattern="^\d{4}-\d{2}$"
                      className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">Submission Deadline *</label>
                    <input type="date" value={form.deadline_at} onChange={e => upd('deadline_at', e.target.value)} required
                      className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Stand Size */}
            <div>
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">Stand Size</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Width (m) *</label>
                  <input type="number" min="1" step="0.5" value={form.w_m} onChange={e => upd('w_m', e.target.value)} required
                    className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Depth (m) *</label>
                  <input type="number" min="1" step="0.5" value={form.d_m} onChange={e => upd('d_m', e.target.value)} required
                    className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
              </div>
              {form.w_m && form.d_m && (
                <p className="text-xs text-orange-600 mt-1.5 font-semibold">
                  Total area: {(parseFloat(form.w_m) || 0) * (parseFloat(form.d_m) || 0)} m²
                </p>
              )}
            </div>

            {/* Budget */}
            <div>
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">Budget</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Min</label>
                  <input type="number" min="0" value={form.budget_min} onChange={e => upd('budget_min', e.target.value)}
                    className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Max *</label>
                  <input type="number" min="1" value={form.budget_max} onChange={e => upd('budget_max', e.target.value)} required
                    className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Currency</label>
                  <select value={form.currency} onChange={e => upd('currency', e.target.value)}
                    className="w-full h-10 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Project Notes / Requirements</label>
              <textarea value={form.notes} onChange={e => upd('notes', e.target.value)} rows={3}
                placeholder="Describe your stand requirements, design preferences, key objectives..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none" />
            </div>

            {error && <p className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 h-11 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={submitting} data-testid="send-direct-brief-btn"
                className="flex-1 h-11 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Brief
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

/* ─── MAIN COMPONENT ────────────────────────────────────── */
const ContractorPublicProfile = () => {
  const { slug } = useParams();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const isExhibitor = user?.org_memberships?.some(m => m.role === 'EXHIBITOR_ADMIN');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/contractor/${slug}`);
        setD(mapApiToData(res.data));
      } catch (e) {
        // Show placeholder data if not found
        setD({ ...DEFAULT_DATA, name: slug?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '—' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );
  if (!d) return null;

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':  return <OverviewTab d={d}
        onProjectClick={(p) => setLightbox({ project: p, startIdx: 0 })}
        onViewAll={() => setActiveTab('portfolio')}
      />;
      case 'portfolio': return <PortfolioTab d={d} />;
      case 'services':  return <ServicesTab d={d} />;
      case 'team':      return <TeamTab d={d} slug={slug} />;
      case 'coverage':  return <CoverageTab d={d} onContactRequest={(msg) => {
        setActiveTab('contact');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('prefillContact', { detail: { message: msg } }));
        }, 200);
      }} />;
      case 'reviews':   return <ReviewsTab d={d} />;
      case 'contact':   return <ContactTab d={d} />;
      default:          return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{d ? `${d.name} — Exhibition Stand Contractor | ExpoStand` : 'Exhibition Stand Contractor | ExpoStand'}</title>
        <meta name="description" content={d ? `${d.name}: ${d.tagline}. ${d.location ? `Based in ${d.location}.` : ''} ${d.coverage?.activeShows?.length > 0 ? `Active at ${d.coverage.activeShows.slice(0,2).join(', ')}.` : ''} Get a free quote today.` : 'Find and contact verified exhibition stand contractors on ExpoStand.'} />
        <meta property="og:title" content={d ? `${d.name} | ExpoStand` : 'Exhibition Stand Contractor | ExpoStand'} />
        <meta property="og:description" content={d?.tagline || 'Professional exhibition stand contractor'} />
        <meta property="og:type" content="profile" />
        {d?.logo && <meta property="og:image" content={d.logo} />}
      </Helmet>
      {d && (<>
      {/* ── Topbar ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-orange-500 transition-colors text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-bold text-orange-500">EXPO</span><span className="font-bold">STAND</span>
          </Link>
          <div className="flex items-center gap-2">
            {isExhibitor ? (
              <button
                data-testid="request-quote-btn"
                onClick={() => setShowQuoteModal(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-1.5"
              >
                <Send className="w-3.5 h-3.5" />
                Request Quote
              </button>
            ) : (
              <button onClick={() => setActiveTab('contact')} className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2 rounded-lg font-semibold transition-colors">
                Submit Brief
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Quote Modal ── */}
      {showQuoteModal && d && (
        <RequestQuoteModal
          contractorOrgId={d.orgId}
          contractorName={d.name}
          onClose={() => setShowQuoteModal(false)}
          token={token}
        />
      )}

      {/* ── Hero — tüm içerik tek solid renkli div içinde ── */}
      <div
        className="relative"
        style={{ backgroundColor: d.coverColor || '#0B1E4D' }}
      >
        {/* Top padding */}
        <div className="h-16 md:h-24" />

        {/* Bottom row: sol = isim+tagline | sağ = logo+tarih */}
        <div className="max-w-6xl mx-auto px-6 pb-6 flex items-end justify-between gap-4">
          {/* Sol — Company name & badges */}
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl md:text-3xl font-black text-white">{d.name}</h1>
              {d.verified && (
                <span className="flex items-center gap-1 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                  <Shield className="w-3 h-3" />Verified
                </span>
              )}
              {d.premium && (
                <span className="flex items-center gap-1 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                  <Award className="w-3 h-3" />Premium
                </span>
              )}
            </div>
            <p className="text-white/75 text-sm">{d.tagline}</p>
          </div>

          {/* Sağ — Logo + sistem tarihi */}
          {d.logo && (
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <img
                src={d.logo}
                alt={d.name + ' logo'}
                style={{ height: '52px', width: 'auto', maxWidth: '200px', objectFit: 'contain' }}
                onError={e => { e.target.parentElement.style.display='none'; }}
              />
              <span className="text-white/60 text-xs font-mono tracking-wide">
                {new Date().toLocaleDateString('tr-TR', { day:'2-digit', month:'2-digit', year:'numeric' })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Meta card — hero altında, beyaz arka planda ── */}
      <div className="max-w-6xl mx-auto px-4 mb-2">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 flex flex-wrap items-center gap-x-6 gap-y-3 -mt-0">
          <div className="flex items-center gap-1.5">
            <Stars rating={d.rating} />
            <span className="font-bold text-gray-900 text-sm">{d.rating}</span>
            <span className="text-gray-400 text-sm">({d.reviewCount} reviews)</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <MapPin className="w-4 h-4 text-gray-400" />{d.location}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <Clock className="w-4 h-4 text-gray-400" />Responds {d.responseTime}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <CheckCircle className="w-4 h-4 text-emerald-500" />Member since {d.memberSince}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <a href={`mailto:${d.email}`} className="w-8 h-8 bg-gray-100 hover:bg-orange-100 rounded-lg flex items-center justify-center transition-colors"><Mail className="w-4 h-4 text-gray-600" /></a>
            <a href={`tel:${d.phone}`} className="w-8 h-8 bg-gray-100 hover:bg-orange-100 rounded-lg flex items-center justify-center transition-colors"><Phone className="w-4 h-4 text-gray-600" /></a>
            <a href={`https://${d.website}`} target="_blank" rel="noreferrer" className="w-8 h-8 bg-gray-100 hover:bg-orange-100 rounded-lg flex items-center justify-center transition-colors"><Globe className="w-4 h-4 text-gray-600" /></a>
          </div>
        </div>
      </div>

      {/* ── Sticky Tabs ── */}
      <div className="sticky top-[57px] z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-none">
            {TABS.map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.id === 'contact' && (
                  <span className="ml-2 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">CTA</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {renderTab()}
      </div>

      {/* ── Overview'dan açılan Lightbox ── */}
      {lightbox && (
        <PortfolioLightbox
          project={lightbox.project}
          startIdx={lightbox.startIdx}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* ── Footer CTA ── */}
      <div className="bg-[#0B1E4D] text-white py-12 mt-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-black mb-2">Ready to work with {d.name}?</h2>
          <p className="text-white/70 mb-6 text-sm">Submit your brief now and receive a detailed proposal within 24 hours.</p>
          <button onClick={() => { setActiveTab('contact'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-xl font-bold transition-colors">
            Submit a Brief
          </button>
        </div>
      </div>
      </>)}
    </div>
  );
};

export default ContractorPublicProfile;
